import React, { useState, useEffect, useCallback } from 'react';
import { getAuthActivity, AuthActivityLog } from '@/api/auth';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIAccountTable, { UIAccountTableColumn } from '@/components/shared/UIAccountTable';
import UIPaginator from '@/components/shared/UIPaginator';
import UIBadge from '@/components/shared/UIBadge';
import { useToast } from '@/components/shared/ToastProvider';
import './SessionAndLoginHistorySection.scss';

export interface SessionAndLoginHistorySectionProps {
  className?: string;
}

const sessionColumns: UIAccountTableColumn[] = [
  {
    key: 'status',
    label: 'STATUS',
    flex: 1,
    minWidth: '80px',
    render: (value: any) => (
      <UIBadge variant={value === 'Success' ? 'success' : 'danger'}>
        {value}
      </UIBadge>
    ),
  },
  {
    key: 'browser',
    label: 'BROWSER',
    flex: 2,
    minWidth: '100px',
  },
  {
    key: 'dateTime',
    label: 'DATE/TIME',
    flex: 2,
    minWidth: '120px',
  },
  {
    key: 'device',
    label: 'DEVICE',
    flex: 1,
    minWidth: '100px',
  },
  {
    key: 'location',
    label: 'LOCATION',
    flex: 1.5,
    minWidth: '120px',
  },
  {
    key: 'ipAddress',
    label: 'IP ADDRESS',
    flex: 1.5,
    minWidth: '100px',
  }
];

const baseClass = 'SessionAndLoginHistorySection';

const SessionAndLoginHistorySection: React.FC<SessionAndLoginHistorySectionProps> = ({
  className = '',
}) => {
  const [logs, setLogs] = useState<AuthActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Keep limit constant
  const [total, setTotal] = useState(0);
  const { showError } = useToast();

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAuthActivity(page, limit);
      const validLogs = response.data.logs ? response.data.logs.filter(log => log) : [];
      setLogs(validLogs);
      setTotal(response.data.total || 0);
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        showError('Unauthorized. Please log in again.');
      } else {
        showError('Failed to fetch auth history.');
      }
      console.error('Failed to fetch auth history:', error);
    }
    setLoading(false);
  }, [page, limit, showError]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formattedData = logs.map(log => {
    let status = 'Failed';
    if (log && log.eventType === 'auth.login.success') {
      status = 'Success';
    }

    let browserName = 'N/A';
    if (log && log.metadata && log.metadata.deviceInfo && log.metadata.deviceInfo.browser && log.metadata.deviceInfo.browser.name) {
      browserName = log.metadata.deviceInfo.browser.name;
    }

    let deviceString = 'N/A';
    if (log && log.metadata && log.metadata.deviceInfo) {
      const deviceInfo = log.metadata.deviceInfo;
      if (deviceInfo.os && deviceInfo.os.name) {
        deviceString = `${deviceInfo.os.name} ${deviceInfo.os.version || ''}`.trim();
      } else if (deviceInfo.device && deviceInfo.device.vendor) {
        deviceString = `${deviceInfo.device.vendor} ${deviceInfo.device.model || ''}`.trim();
      }
    }

    let locationString = 'N/A';
    if (log && log.metadata && log.metadata.location && log.metadata.location.city && log.metadata.location.country) {
      locationString = `${log.metadata.location.city}, ${log.metadata.location.country}`;
    }

    let ipAddress = 'N/A';
    if (log && log.metadata && log.metadata.ip) {
      ipAddress = log.metadata.ip;
    }
    
    let dateTime = 'N/A';
    if (log && log.createdAt) {
        dateTime = new Date(log.createdAt).toLocaleString();
    }

    return {
      status: status,
      browser: browserName,
      dateTime: dateTime,
      device: deviceString,
      location: locationString,
      ipAddress: ipAddress,
    };
  });

  const totalPages = Math.ceil(total / limit);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title="Session and login history"
          description=""
        />
      </UISectionHeader>

      <UISectionBody>
        <div className={`${baseClass}__tables`}>
          <div className={`${baseClass}__content`}>
            <div className={`${baseClass}__login-history`}>
              <h3 className={`${baseClass}__section-title`}>Login History</h3>
              {loading ? (
                <div>Loading...</div>
              ) : (
                <UIAccountTable
                  columns={sessionColumns}
                  data={formattedData}
                  headerVariant="light"
                  className={`${baseClass}__table`}
                />
              )}
              <UIPaginator
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </UISectionBody>
    </UISectionWrapper>
  );
};

export default SessionAndLoginHistorySection;
